/**
 * Member Price – PDP automatic discount display
 *
 * Detects Shopify automatic discounts for logged-in EcoFlow members
 * and updates prices across the PDP (main price, variant options, ATC total).
 *
 * Requirements:
 *   - Price element must have data-mp-variant and data-mp-price attributes
 *   - Load this script with `defer` so the DOM is fully parsed on execution
 *   - Relies on global `theme.Currency.formatMoney` and related theme settings
 */
(function () {
  'use strict';

  if (document.cookie.indexOf('userInfo=') === -1) return;

  var priceEl = document.querySelector('[data-price-element][data-mp-variant]');
  if (!priceEl) return;

  var CACHE_KEY = '_mp_discount';
  var CACHE_TTL = 5 * 60 * 1000;

  var initVariantId = parseInt(priceEl.dataset.mpVariant, 10);
  var initPrice = parseInt(priceEl.dataset.mpPrice, 10);
  if (!initVariantId || !initPrice) return;

  var discountCache = null;
  var originalPrices = {};
  originalPrices[initVariantId] = initPrice;

  console.log('[MemberPrice] init, variant:', initVariantId, 'price:', initPrice);

  // --------------- sessionStorage cache ---------------

  function loadCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (Date.now() - obj.ts > CACHE_TTL) {
        sessionStorage.removeItem(CACHE_KEY);
        return null;
      }
      return obj.data;
    } catch (e) {
      return null;
    }
  }

  function saveCache(data) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: data }));
    } catch (e) { /* quota exceeded – ignore */ }
  }

  // --------------- helpers ---------------

  function getVariantShowPrice(variant) {
    var showPrice = variant.price;
    var metaEl = document.querySelector('[data-variants-custommetafields-json]');
    if (metaEl) {
      try {
        var metaData = JSON.parse(metaEl.innerHTML);
        if (metaData[variant.id]) showPrice = metaData[variant.id];
      } catch (ex) { /* ignore */ }
    }
    return showPrice;
  }

  function calcDiscountCents(priceCents) {
    if (!discountCache) return 0;
    if (discountCache.valueType === 'percentage') {
      return Math.round(priceCents * discountCache.value / 100);
    }
    return Math.round(discountCache.value * 100);
  }

  // --------------- DOM updates ---------------

  function applyDiscount(variantId) {
    if (!discountCache || discountCache.value <= 0) return;

    var fmt = theme.priceCurrencyCodeEnable ? theme.moneyFormatWithCurrency : theme.moneyFormat;
    var cmpFmt = theme.comparePriceCurrencyCodeEnable ? theme.moneyFormatWithCurrency : theme.moneyFormat;

    var saleEl = priceEl.querySelector('[data-sale-price]');
    var regEls = priceEl.querySelectorAll('[data-regular-price]');
    var savEl = priceEl.querySelector('[data-savings]');

    var curPrice = originalPrices[variantId];
    if (!curPrice || curPrice <= 0) return;

    var discountCents = calcDiscountCents(curPrice);
    if (discountCents <= 0) return;
    var memberPrice = curPrice - discountCents;
    if (memberPrice <= 0) return;

    if (!priceEl.classList.contains('price--on-sale')) {
      priceEl.classList.add('price--on-sale');
    }
    if (saleEl) saleEl.innerHTML = theme.Currency.formatMoney(memberPrice, fmt);
    regEls.forEach(function (el) {
      el.innerHTML = theme.Currency.formatMoney(curPrice, cmpFmt);
    });
    if (savEl) savEl.innerHTML = '-' + theme.Currency.formatMoney(discountCents, theme.moneyFormat);

    var submitWrapper = document.querySelector('.product-form__controls-group--submit .price-wrapper');
    if (submitWrapper) {
      var qtyInput = document.querySelector('[name="quantity"]');
      var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

      submitWrapper.setAttribute('data-price', memberPrice);
      submitWrapper.setAttribute('data-compare-price', curPrice);
      submitWrapper.setAttribute('data-saving', discountCents);

      var submitReg = submitWrapper.querySelector('.regular-price');
      var submitCmp = submitWrapper.querySelector('.compare-price');
      var submitSav = submitWrapper.querySelector('.total-savings');
      if (submitReg) submitReg.innerHTML = theme.Currency.formatMoney(memberPrice * qty, fmt);
      if (submitCmp) {
        submitCmp.innerHTML = theme.Currency.formatMoney(curPrice * qty, cmpFmt);
        submitCmp.style.display = '';
      }
      if (submitSav) {
        submitSav.innerHTML = '-' + theme.Currency.formatMoney(discountCents * qty, theme.moneyFormat);
        submitSav.style.display = '';
      }
    }

    console.log('[MemberPrice] applied:', discountCache.title, curPrice, '->', memberPrice);
  }

  function applySwatchDiscounts() {
    if (!discountCache || discountCache.value <= 0) return;
    var fmt = theme.priceCurrencyCodeEnable ? theme.moneyFormatWithCurrency : theme.moneyFormat;
    var cmpFmt = theme.comparePriceCurrencyCodeEnable ? theme.moneyFormatWithCurrency : theme.moneyFormat;

    document.querySelectorAll('.swatch-price').forEach(function (sp) {
      var regPrice = sp.querySelector('.regular-price');
      if (!regPrice) return;

      var priceCents = parseInt(sp.getAttribute('data-original-cents'), 10);
      if (!priceCents) {
        var priceText = regPrice.textContent.replace(/[^0-9.]/g, '');
        priceCents = Math.round(parseFloat(priceText) * 100);
        if (priceCents > 0) sp.setAttribute('data-original-cents', priceCents);
      }
      if (!priceCents || priceCents <= 0) return;

      var discountCents = calcDiscountCents(priceCents);
      if (discountCents <= 0 || discountCents >= priceCents) return;

      var newPrice = priceCents - discountCents;
      var cmpEl = sp.querySelector('.compare-price');
      if (!cmpEl) {
        cmpEl = document.createElement('s');
        cmpEl.className = 'compare-price';
        regPrice.parentNode.insertBefore(cmpEl, regPrice.nextSibling);
      }
      cmpEl.innerHTML = theme.Currency.formatMoney(priceCents, cmpFmt);
      cmpEl.style.display = '';
      regPrice.innerHTML = theme.Currency.formatMoney(newPrice, fmt);
    });

    console.log('[MemberPrice] swatch prices updated');
  }

  // --------------- Cart API with 429 retry ---------------

  function wait(ms) {
    return new Promise(function (r) { setTimeout(r, ms); });
  }

  async function cartFetch(url, opts, retries) {
    retries = retries || 3;
    for (var i = 0; i < retries; i++) {
      var res = await fetch(url, opts);
      if (res.status === 429) {
        console.warn('[MemberPrice] 429, retry', i + 1);
        await wait(1500 * (i + 1));
        continue;
      }
      if (!res.ok) throw new Error('HTTP ' + res.status);
      var text = await res.text();
      try {
        return JSON.parse(text);
      } catch (e) {
        if (i < retries - 1) { await wait(500); continue; }
        throw new Error('Invalid JSON');
      }
    }
    throw new Error('Max retries for ' + url);
  }

  // --------------- discount detection ---------------

  async function detectDiscount(variantId) {
    var cached = loadCache();
    if (cached) {
      discountCache = cached;
      console.log('[MemberPrice] loaded from cache:', cached);
      return;
    }

    var addedKey = null;
    var originalQty = 0;

    try {
      var originalCart = await cartFetch('/cart.js', { credentials: 'same-origin' });
      var originalItem = originalCart.items.find(function (i) { return i.variant_id == variantId; });
      originalQty = originalItem ? originalItem.quantity : 0;

      await wait(500);
      await cartFetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });

      await wait(500);
      var cart = await cartFetch('/cart.js', { credentials: 'same-origin' });
      var item = cart.items.find(function (i) { return i.variant_id == variantId; });

      if (item) {
        addedKey = item.key;
        var allocs = item.line_level_discount_allocations || item.discount_allocations || [];
        if (allocs.length > 0) {
          var da = allocs[0];
          var dApp = da.discount_application || {};
          discountCache = {
            value: parseFloat(dApp.value) || 0,
            valueType: dApp.value_type || 'percentage',
            title: dApp.title || 'Discount'
          };
        } else {
          discountCache = { value: 0, valueType: 'percentage', title: '' };
        }

        saveCache(discountCache);
        console.log('[MemberPrice] detected & cached:', discountCache);

        await wait(500);
        await cartFetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: addedKey, quantity: originalQty })
        });
      }
    } catch (e) {
      console.error('[MemberPrice] detect error:', e.message);
      if (addedKey) {
        try {
          await wait(1000);
          await cartFetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: addedKey, quantity: originalQty })
          });
        } catch (e2) { /* best-effort restore */ }
      }
    }
  }

  // --------------- bootstrap ---------------

  detectDiscount(initVariantId).then(function () {
    applyDiscount(initVariantId);
    applySwatchDiscounts();
  });

  var sectionEl = priceEl.closest('[data-section-id]');
  (sectionEl || document).addEventListener('variantPriceChange', function (e) {
    if (!e.detail || !e.detail.variant) return;
    var v = e.detail.variant;
    if (!originalPrices[v.id]) {
      originalPrices[v.id] = getVariantShowPrice(v);
    }
    setTimeout(function () { applyDiscount(v.id); }, 80);
  });
})();
